/**
 * schemaToExample — walk a JSON Schema (as produced by zod-to-json-schema)
 * and synthesize a plausible example payload. Used by the Trigger modal's
 * JSON input to pre-fill the editor so users see *what shape* the
 * endpoint expects rather than a blank "{}".
 *
 * Preference order for each field:
 *   1. schema.default          ← author already declared the canonical example
 *   2. schema.examples[0]      ← Zod's .describe() can hang examples here
 *   3. schema.const            ← single literal
 *   4. type-based stub         ← '' / 0 / false / [] / {}
 *
 * Required-only mode (`mode: 'required'`) emits only required fields, so
 * the example is a minimal payload the user can submit as-is. Default
 * (`mode: 'full'`) emits every declared field, so users see the full
 * surface and can delete what they don't need.
 *
 * Refuses to recurse infinitely on $ref-cycles; bails out at depth 4.
 */

const MAX_DEPTH = 4;

// Description-prefix convention for fields that a user should NOT see/fill in a
// MANUAL trigger. Two classes, both keyed off the field's `.describe(...)` text
// (lower-cased + trimmed) so this stays GENERIC — no hardcoded field names, any
// template gets the same treatment just by prefixing its description:
//   - "self-host only"   → threaded in only by the self-hosted runner.
//   - "comment_reply only" → set only by the webhook on the in-thread reply path.
const TRIGGER_HIDDEN_PREFIXES = ['self-host only', 'comment_reply only'];

/**
 * isTriggerHidden — true when a property's description marks it as not
 * user-fillable in a manual trigger (see TRIGGER_HIDDEN_PREFIXES).
 *
 * Robust: only a string description that STARTS WITH one of the exact phrases
 * (case-insensitive) is hidden. A field without a description is never hidden,
 * so required fields that happen to lack a description stay visible.
 *
 * @param {object} propSchema  a single property's JSON Schema node
 * @returns {boolean}
 */
export function isTriggerHidden(propSchema) {
  const desc = propSchema?.description;
  if (typeof desc !== 'string') return false;
  const normalized = desc.trim().toLowerCase();
  return TRIGGER_HIDDEN_PREFIXES.some((p) => normalized.startsWith(p));
}

function exampleFor(schema, depth = 0, mode = 'full', hideInternal = false) {
  if (!schema || typeof schema !== 'object' || depth > MAX_DEPTH) return null;

  // Authored hints win first.
  if (schema.default !== undefined) return schema.default;
  if (Array.isArray(schema.examples) && schema.examples.length > 0) return schema.examples[0];
  if (schema.const !== undefined) return schema.const;
  if (Array.isArray(schema.enum) && schema.enum.length > 0) return schema.enum[0];

  const t = schema.type;

  if (t === 'string') {
    if (schema.format === 'uri' || schema.format === 'url') return 'https://example.com';
    if (schema.format === 'email') return 'user@example.com';
    if (schema.format === 'date-time') return new Date().toISOString();
    return '';
  }
  if (t === 'integer') return 0;
  if (t === 'number') return 0;
  if (t === 'boolean') return false;

  if (t === 'array') {
    // Emit one example element so the shape is visible, not just `[]`.
    if (schema.items) {
      const item = exampleFor(schema.items, depth + 1, mode, hideInternal);
      return item === null ? [] : [item];
    }
    return [];
  }

  if (t === 'object' && schema.properties) {
    const out = {};
    const required = new Set(schema.required || []);
    for (const [k, v] of Object.entries(schema.properties)) {
      // A field with a `default` is satisfiable WITHOUT user input (the default
      // applies), so for the trigger payload it's effectively OPTIONAL even when
      // the JSON-schema `required` array lists it (Zod `.default()` fields land
      // in `required` but shouldn't need to be filled). Required-only mode emits
      // only fields that are required AND have no default.
      if (mode === 'required' && (!required.has(k) || v.default !== undefined)) continue;
      // Opt-in: omit trigger-hidden props (self-host / comment_reply) from the
      // generated payload so a manual trigger only shows fillable fields.
      if (hideInternal && isTriggerHidden(v)) continue;
      const val = exampleFor(v, depth + 1, mode, hideInternal);
      if (val !== null) out[k] = val;
    }
    return out;
  }

  // anyOf / oneOf — pick the first branch.
  for (const key of ['anyOf', 'oneOf', 'allOf']) {
    if (Array.isArray(schema[key]) && schema[key].length > 0) {
      return exampleFor(schema[key][0], depth + 1, mode, hideInternal);
    }
  }

  return null;
}

/**
 * Public entrypoint. Returns a JS object (or `{}` for null/empty schemas).
 *
 * @param {object|null} schema  JSON Schema (zod-to-json-schema output)
 * @param {object}      opts
 * @param {string}      [opts.mode='full']        'full' | 'required'
 * @param {boolean}     [opts.hideInternal=false] when true, omit trigger-hidden
 *        props (self-host-only / comment_reply-only) recursively. Off by
 *        default so existing callers are unaffected.
 */
export function schemaToExample(schema, { mode = 'full', hideInternal = false } = {}) {
  if (!schema) return {};
  const result = exampleFor(schema, 0, mode, hideInternal);
  return result && typeof result === 'object' && !Array.isArray(result) ? result : {};
}

/**
 * Pretty-print a value as 2-space-indented JSON, never throwing on
 * circular refs (uses a safe replacer).
 */
export function prettyJson(value) {
  const seen = new WeakSet();
  return JSON.stringify(value, (_k, v) => {
    if (typeof v === 'object' && v !== null) {
      if (seen.has(v)) return '[Circular]';
      seen.add(v);
    }
    return v;
  }, 2);
}
